import { z } from 'zod';
import {
  draftRegistrationSchema,
  settingsSchema,
  suggestedRegistrationSchema,
} from './schemas';

type DraftRegistration = z.infer<typeof draftRegistrationSchema>;
type SuggestedRegistration = z.infer<typeof suggestedRegistrationSchema>;
type OfferedCustomFields = z.infer<
  typeof settingsSchema
>['registrations']['customFields'];

export const draftRegistrationToCreate = ({
  draft,
  suggested,
  referrer: referrerIn,
  storeProductVariants,
}: {
  draft: DraftRegistration;
  suggested?: SuggestedRegistration | null;
  referrer?: string | null;
  storeProductVariants: {
    id: number;
    quantity: number;
  }[];
}) => {
  if (!draft.storeSalesChannel)
    throw new Error('Store sales channel is required');

  const { proofOfPurchaseAssets, storeSalesChannel, ...forCreate } = draft;
  const address = draft.address
    ? {
        ...draft.address,
        firstName: draft.firstName,
        lastName: draft.lastName,
      }
    : undefined;

  const proofOfPurchaseFilenames = proofOfPurchaseAssets
    ? proofOfPurchaseAssets.map((asset) => asset.cdnName)
    : [];

  const customFields = Object.entries(draft.customFields).map(
    ([id, { type, value }]) => ({
      id: Number(id),
      value:
        type === 'Date' ? new Date(`${value}T00:00:00Z`).toISOString() : value,
    }),
  );
  const referrer = referrerIn ?? undefined;
  const suggestedRegistration = suggested ? { ...suggested, referrer } : null;

  return {
    ...forCreate,
    storeSalesChannelId: storeSalesChannel.id,
    suggestedRegistration,
    storeProductVariants,
    address,
    proofOfPurchaseFilenames,
    referrer,
    customFields,
  };
};

export const suggestedRegistrationToDraft = (
  draft: DraftRegistration | null,
  suggested: SuggestedRegistration | null,
  offeredCustomFields: OfferedCustomFields,
) => {
  if (draft) return draft;
  if (!suggested) return null;

  const { customFields: suggestedRegCustomFields } = suggested;

  const appliedCustomFields = suggestedRegCustomFields
    ? offeredCustomFields
        .map((f) => {
          const raw = suggestedRegCustomFields.find((cf) => cf.id === f.id);
          if (!raw) return null;
          return {
            id: f.id,
            value: raw.value,
            type: f.valueType,
          };
        })
        .filter(Boolean)
    : [];

  const {
    email,
    firstName,
    lastName,
    storeSalesChannel,
    estimatedPurchaseDate,
    orderNumber,
    address,
    proofOfPurchaseAssets,
  } = suggested;

  return {
    email: email ?? '',
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    storeSalesChannel,
    estimatedPurchaseDate: estimatedPurchaseDate ?? '',
    proofOfPurchaseAssets: proofOfPurchaseAssets ?? [],
    emailMarketingConsentCollectedOn: undefined,
    orderNumber,
    address: address
      ? {
          line1: address.line1 ?? '',
          line2: address.line2 ?? '',
          city: address.city ?? '',
          stateOrProvinceCode: address.stateOrProvinceCode ?? '',
          // optional field: keep it absent rather than writing an empty postal code
          postalCode: address.postalCode ?? undefined,
          countryCode: address.countryCode ?? '',
        }
      : undefined,
    customFields: Object.fromEntries(
      appliedCustomFields.map((field) => [
        field.id,
        { value: field.value, type: field.type },
      ]),
    ),
  } satisfies DraftRegistration;
};
